import React, { useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import PropTypes from 'prop-types';

// Third party components
import { Box, Card, CardContent, Container, Grid, Stack, Typography } from '@mui/material';

// Shared components
import { LineLoader, SubHeader, Button, LoadingSpinner, ContainerWithCard } from '@timmons-group/shared-react-components';

import AnyField from 'components/AnyField';

// Hooks
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
// import { useLayout } from '@timmons-group/shared-react-components/hooks';
import { useLayout } from '../../hooks/useLayout';
import { processLayout } from '@timmons-group/shared-react-components/helpers';
import { useSnackbar } from 'notistack';
import { object } from 'yup';
import axios from 'axios';
import { useRecipient, ACTIONS } from '../../hooks/useRecipient';
import { FIELD_TYPES } from 'constants';

const EditRecipient = ({ id }) => {
  // This hook lets us interact with remote recipient data via our REST API
  const { recipientState, dispatch } = useRecipient();

  // Unpack specific state values
  const { loading, recipient } = recipientState;

  // Make a copy of the fetched recipient state
  const [currentRecipient, setCurrentRecipient] = useState(null);

  // This runs when the component mounts and if the query param id changes
  // It will go fetch the recipient data from the API with that id
  useEffect(() => {
    // Force a refresh of the data if id is in the URL - only for edits
    if (id) {
      dispatch({ type: ACTIONS.SET_ID, payload: id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // This will run when on an edit endpoint because the SET_ID action above will trigger
  // a loading state when fetching new data. When the data is fetched, it will attempt to
  // hydrate form data.
  useEffect(() => {
    if (!loading && recipient) {
      document.title = `Edit Recipient ${recipient.name}`;
      const optionalFields = ['middleName', 'notes', 'email', 'phone', 'addrCity', 'addrState', 'addrStreet', 'addrZip'];
      optionalFields.forEach(field => {
        if (!recipient[field]) {
          recipient[field] = '';
        }
      });
      setCurrentRecipient({...recipient});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Show the user the line loader while the request is fetching
  if (loading) {
    return <LineLoader message="Loading..." />
  }

  // If no recipient is found, show the user a message
  if (!currentRecipient) {
    return <div>No recipient found</div>
  }

  // If we have a recipient, render the form in edit mode
  return (
    <>
      <TheForm
        isEdit={true}
        currentRecipient={currentRecipient}
        formTitle="Edit Recipient"
        cancelUrl={'/recipients'}
        id={id}
      />
    </>
  );
}

EditRecipient.propTypes = {
  id: PropTypes.string.isRequired,
}

const TheForm = ({ id, formTitle, cancelUrl, successUrl, isEdit, currentRecipient }) => {
  const [layout, layoutLoading, parsedLayout] = useLayout('recipient', 'new');

  const [modifying, setModifying] = useState(false);
  const nav = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [sections, setSections] = useState([]);
  const [hasWatches, setHasWatches] = useState(false);
  const [validations, setValidations] = useState({
  });
  const [tempDisable, setTempDisabled] = useState(false);

  // update the validation schema hookForm uses when the validation state changes
  const validationSchema = useMemo(
    () => {
      return object({ ...validations });
    },
    [validations]
  );

  // If the schema changes and the form has been submitted, revalidate
  useEffect(() => {
    if (formState.isSubmitted) {
      trigger();
    }
    if (isEdit && formState.defaultValues !== currentRecipient) {
      reset(currentRecipient);
    }
    // We do not want formState.isSubmitted to be a dependency here, the trigger happens on form submit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationSchema]);

  const { control, handleSubmit, formState, watch, trigger, setValue, reset, resetField } = useForm({
    defaultValues: isEdit && currentRecipient ? currentRecipient : {
    },
    mode: 'onBlur',
    resolver: yupResolver(validationSchema)
  });

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      console.log(value, name, type)
      if (value?.underserved?.id === 1) {
        setTempDisabled(false);
      } else {
        setTempDisabled(true);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Temp disable is a workaround - whenever it changes check if we should disable the tribal field
  useEffect(() => {
    if (tempDisable) {
      setValue('tribal', {id: 2, name: 'No'});
    }
  }, [tempDisable]);

  useEffect(() => {
    if (layoutLoading) {
      return;
    }

    const layoutSections = processLayout(layout);
    const dynValid = {};
    let watchMe = false;
    parsedLayout.sections.forEach(section => {
      for (const fieldId of section.fields) {
        if (parsedLayout.fields.has(fieldId)) {
          const field = parsedLayout.fields.get(fieldId);
          dynValid[field.id] = field.validations;
          if (parsedLayout.triggerFields.has(fieldId)) {
            watchMe = true;
          }
          if (field.render.defaultValue) {
            setValue(field.render.name, field.render.defaultValue);
          }
        }
      }
    });

    if (watchMe) {
      setHasWatches(watchMe);
    }

    if (Object.keys(dynValid).length > 0) {
      setValidations((prevValues) => {
        return {
          ...dynValid,
        };
      });
    }

    for (const section of layoutSections) {
      section.fields.forEach(({ render }) => {
        // Preselect the first option if there is only one option
        // const render = field.render;
        if (render.choices?.length === 1) {
          setValue(render.name, render.choices[0]);
        }
        if (render.name === 'state') {
          render.disabled = true;
        }
      });
    }
    setSections(layoutSections);

    // We do this to cause any watched fields to fire on initial load
    // This will also set the sections.
    if (isEdit) {
      reset();
    }

    // We really only want to run this on layoutLoading changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutLoading]);

  useEffect(() => {
    let subscription = null;
    if (hasWatches) {
      subscription = watch((value, { name, type }) => {
        const revalidates = {};
        const triggerField = parsedLayout.triggerFields.get(`fields.${name}`);
        if (!triggerField || type !== 'change') {
          return;
        }

        const triggerFieldType = parsedLayout.fields.get(triggerField.id).type;

        let formValue = value[name];
        if (triggerFieldType === FIELD_TYPES.Object || triggerFieldType === FIELD_TYPES.CHOICE) {
          formValue = formValue?.id;
        }

        const updatedFields = [];

        if (triggerField.fieldValues.has(formValue)) {
          const affectedFields = triggerField.fieldValues.get(formValue);
          affectedFields.forEach((loadOut, fieldId) => {
            updatedFields.push({ id: fieldId, type: 'update', ...loadOut });
          });
        }

        const touchedFields = triggerField.touches;
        touchedFields.forEach((value, fieldId) => {
          if (!value.has(formValue)) {
            updatedFields.push({ id: fieldId, type: 'reset' });
          }
        });

        const dynValid = {};
        const resetFields = {};
        const layoutSections = sections.map(section => {
          updatedFields.forEach(field => {
            const fieldObject = parsedLayout.fields.get(field.id);
            const fndField = section.fields.find(x => x.render.name === fieldObject.id);
            if (fndField) {
              revalidates[fieldObject.id] = true;
              let { render } = fndField;
              if (field.type === 'update') {
                dynValid[fieldObject.id] = field.validation;
                const updatedLayout = Object.fromEntries(field.layout);
                render = { ...render, ...updatedLayout };
              } else {
                dynValid[fieldObject.id] = fieldObject.validations;
                render = { ...render, ...fieldObject.render };
              }

              fndField.render = render;

              if (fndField.render.disabled) {
                resetFields[fieldObject.id] = true;
              }
            }
          });
          return section;
        });

        setSections(layoutSections);

        // This will trigger the useMemo to update the validation schema
        // That hook will then trigger the useEffect to revalidate the form
        setValidations((prevValues) => {
          return {
            ...prevValues,
            ...dynValid,
          };
        });

        // This will reset any fields that were disabled
        // We need to bypass the batch updates here or the disabled fields may trigger their own validation before the schema update occurs
        // and out validation appearance will be out of sync with the schema
        flushSync(() => {
          for (const field in resetFields) {
            resetField(field);
          }
        });

      });
    }
    return () => {
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasWatches]);

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      type: parseInt(data.type?.id),
      state: parseInt(data.state?.id),
    };

    addOrEditRecipient(payload, isEdit, successUrl, cancelUrl);
  }

  const addOrEditRecipient = async (currentRecipient, edit, successUrl, cancelUrl) => {
    setModifying(true);
    try {
      const result = await axios.post(`/api/recipient/${edit ? 'update' : 'new'}`, currentRecipient);
      if (result?.data?.streamID) {
        setTimeout(() => {
          enqueueSnackbar(`Recipient "${currentRecipient.firstName} ${currentRecipient.lastName}" successfully ${edit ? 'updated' : 'created'}`, { variant: 'success' });
          nav(successUrl || cancelUrl);
        }, 1000);
      } else {
        setModifying(false);
        enqueueSnackbar(`Error ${edit ? 'updating' : 'creating'} Recipient "${currentRecipient.firstName} ${currentRecipient.lastName}"`, { variant: 'error' });
      }
    } catch (error) {
      if (error?.response?.data?.error) {
        enqueueSnackbar(`${error.response.data.error}`, { variant: 'error' });
      } else {
        enqueueSnackbar(`Error ${edit ? 'updating' : 'creating'} Recipient "${currentRecipient.firstName} ${currentRecipient.lastName}"`, { variant: 'error' });
      }
      setModifying(false);
    } finally {
      setModifying(false);
    }
  };

  const renderField = (field, control, index, tempDisable) => {
    return (
      <AnyField sx={{ marginTop: index ? '16px' : null }} key={index} layout={field.render} control={control} tempDisable={tempDisable} />
    )
  }

  // onSubmit is not called if the form is invalid
  // so we need to manually check for this
  const preSubmit = (evt) => {
    handleSubmit(onSubmit)(evt);
  }

  const createFormSection = (section, control, index, tempDisable) => {
    const nextCol = Math.ceil(section.fields.length / 2);
    const left = section.fields.slice(0, nextCol);
    const right = section.fields.slice(nextCol);

    return (
      <div key={index}>
        <Grid
          container
          spacing={{ xs: 1, sm: 2, md: 4 }}
        >
          <Grid item xs={6}>
            {left.map((field, index) => renderField(field, control, index, tempDisable))}
          </Grid>
          <Grid item xs={6}>
            {right.map((field, index) => renderField(field, control, index, tempDisable))}
          </Grid>
        </Grid>
      </div>
    )
  }

  const theForm = (tempDisable) => {
    return (
      <form>
        {sections.map((field, index) => {
          return createFormSection(field, control, index, tempDisable);
        })}
      </form>
    );
  }

  const rendered = (tempDisable) => {
    if (layoutLoading) {
      return (
        <ContainerWithCard>
          <LineLoader message="Loading..." />
        </ContainerWithCard>
      )
    }

    const formDetailsText = isEdit? 'Please update Recipient details.' :
      'Please enter the required details to create a new Recipient.';

    return (
      <>
        <SubHeader
          title={formTitle}
          rightRender={
            () =>
              <Stack spacing={2} direction="row" justifyContent="flex-end">
                <Button color="primary" href={cancelUrl} label="Cancel" />
                {/* {isEdit && <Button color="secondary" onClick={() => reset()} label={'Reset'} />} */}
                <Button color="secondary" onClick={preSubmit} label="Save" />
              </Stack>
          }
        />
        <LoadingSpinner isActive={modifying} />
        <Box px={7}>
          <Container sx={{ position: 'relative', marginTop: '16px' }}
            maxWidth={false}>
            <Card sx={{ position: 'relative' }}>
              <CardContent sx={{ paddingBottom: '0px' }}>
                <Typography variant="sectionHeader">Recipient Details</Typography>
              </CardContent>
              <hr />
              <CardContent>
                <p>{formDetailsText}</p>
                {theForm(tempDisable)}
              </CardContent>
            </Card>
          </Container>
        </Box>
      </>
    );
  }

  return rendered(tempDisable);
}

const RecipientForm = () => {
  const { streamID } = useParams();
  const isEdit = streamID !== undefined;

  useEffect(() => {
    document.title = isEdit ? 'Edit Recipient' : 'Add Individual Recipient';
  }, [isEdit]);

  const rendered = () => {
    if (isEdit) {
      return <EditRecipient id={streamID} />
    }

    return (
      <>
        <TheForm
          formTitle="Add Individual Recipient"
          cancelUrl={'/recipients'}
          id={streamID}
        />
      </>
    );
  }

  return rendered();
}

export default RecipientForm;
